import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { type IAddress, type ICountry } from '@mahawi/eshop-common/src/types';
import {
  Button,
  Col,
  Drawer,
  Form,
  Grid,
  Input,
  Row,
  Select,
  Space,
  Tag,
} from 'antd';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';

import { getCountryOptions } from '../fragments/get-country-options';
import { useAppDispatch } from '../hooks/use-app-dispatch';
import { type RootState } from '../reducers';
import {
  emailValidatorReset,
  emailValidatorVerify,
} from '../reducers/email-validator/reducer';
import { SPACING } from '../theme/tokens';

export const ADDRESS_EDIT_DRAWER_CORRELATION_ID = 'addressEditDrawer';

interface IAddressEditDrawerProps {
  open: boolean;
  onClose: () => void;
  onSave: (address: IAddress) => void;
  title: string;
  address: IAddress;
  countries: ICountry[] | undefined;
  showCompanyFields: boolean;
  inProcess: boolean;
}

export function AddressEditDrawer({
  open,
  onClose,
  onSave,
  title,
  address,
  countries,
  showCompanyFields,
  inProcess,
}: IAddressEditDrawerProps): React.ReactElement {
  const { md: isDesktop } = Grid.useBreakpoint();
  const [form] = Form.useForm<IAddress>();
  const dispatch = useAppDispatch();
  const emailValue: string | undefined = Form.useWatch('email', form);
  const verifyEntry = useSelector(
    (s: RootState) =>
      s.EmailValidator.entries[ADDRESS_EDIT_DRAWER_CORRELATION_ID],
  );
  const verifyInProcess: boolean = verifyEntry?.inProcess ?? false;
  const verifyResult = verifyEntry?.result;

  useEffect((): void => {
    if (open) {
      form.setFieldsValue(address);
      dispatch(emailValidatorReset(ADDRESS_EDIT_DRAWER_CORRELATION_ID));
    }
  }, [open, address, form, dispatch]);

  const handleFinish = (values: IAddress): void => {
    onSave(values);
    onClose();
  };

  const handleVerifyEmail = (): void => {
    if (emailValue) {
      dispatch(
        emailValidatorVerify(ADDRESS_EDIT_DRAWER_CORRELATION_ID, emailValue),
      );
    }
  };

  const handleUseSuggestedEmail = (): void => {
    if (verifyResult?.suggestedEmail) {
      form.setFieldValue('email', verifyResult.suggestedEmail);
      dispatch(emailValidatorReset(ADDRESS_EDIT_DRAWER_CORRELATION_ID));
    }
  };

  return (
    <Drawer
      styles={{ wrapper: { width: isDesktop ? '40%' : '90%' } }}
      title={title}
      placement="right"
      onClose={onClose}
      open={open}
      destroyOnHidden
    >
      <Form form={form} layout="vertical" onFinish={handleFinish}>
        <Row gutter={SPACING.lg}>
          <Col span={12}>
            <Form.Item name="name" label="Name" rules={[{ required: true }]}>
              <Input disabled={inProcess} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="surname"
              label="Surname"
              rules={[{ required: true }]}
            >
              <Input disabled={inProcess} />
            </Form.Item>
          </Col>
        </Row>

        {showCompanyFields && (
          <>
            <Form.Item name="companyName" label="Company name">
              <Input disabled={inProcess} />
            </Form.Item>

            <Row gutter={SPACING.lg}>
              <Col span={12}>
                <Form.Item name="companyId" label="Company ID">
                  <Input disabled={inProcess} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="companyTaxId" label="Company VAT ID">
                  <Input disabled={inProcess} />
                </Form.Item>
              </Col>
            </Row>
          </>
        )}

        <Row gutter={SPACING.lg}>
          <Col span={16}>
            <Form.Item
              name="street"
              label="Street"
              rules={[{ required: true }]}
            >
              <Input disabled={inProcess} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="streetNumber"
              label="Street number"
              rules={[{ required: true }]}
            >
              <Input disabled={inProcess} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={SPACING.lg}>
          <Col span={12}>
            <Form.Item name="city" label="City" rules={[{ required: true }]}>
              <Input disabled={inProcess} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="zip" label="Zip" rules={[{ required: true }]}>
              <Input disabled={inProcess} />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="countryTypeIsoCode"
          label="Country"
          rules={[{ required: true }]}
        >
          <Select
            showSearch={{ optionFilterProp: 'label' }}
            disabled={inProcess}
            options={getCountryOptions(countries)}
          />
        </Form.Item>

        <Row gutter={SPACING.lg}>
          <Col span={12}>
            <Form.Item name="phone" label="Phone" rules={[{ required: true }]}>
              <Input disabled={inProcess} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="email"
              label="Email"
              rules={[{ required: true }, { type: 'email' }]}
            >
              <Input disabled={inProcess} />
            </Form.Item>

            <Space
              orientation="vertical"
              size={SPACING.sm}
              style={{ width: '100%', marginBottom: SPACING.md }}
            >
              <Button
                size="small"
                loading={verifyInProcess}
                disabled={inProcess || verifyInProcess || !emailValue}
                onClick={handleVerifyEmail}
              >
                Verify email
              </Button>

              {verifyResult && (
                <Space wrap size={SPACING.sm}>
                  {verifyResult.isValid === true && (
                    <Tag color="success" icon={<CheckCircleOutlined />}>
                      Valid
                    </Tag>
                  )}
                  {verifyResult.isValid === false && (
                    <Tag color="error" icon={<CloseCircleOutlined />}>
                      Invalid
                    </Tag>
                  )}
                  {verifyResult.isValid === null && (
                    <Tag color="warning" icon={<WarningOutlined />}>
                      Inconclusive
                    </Tag>
                  )}
                  {verifyResult.isDisposable && (
                    <Tag color="warning">Disposable</Tag>
                  )}
                  {verifyResult.suggestedEmail && (
                    <Button
                      type="link"
                      size="small"
                      onClick={handleUseSuggestedEmail}
                    >
                      Use &quot;{verifyResult.suggestedEmail}&quot; instead?
                    </Button>
                  )}
                </Space>
              )}
            </Space>
          </Col>
        </Row>

        <Button
          type="primary"
          htmlType="submit"
          loading={inProcess}
          disabled={inProcess}
        >
          Save changes
        </Button>
      </Form>
    </Drawer>
  );
}
